import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';

/**
 * Wiersz listy odbiorców - odpowiedź Admin::getStatsRecipients() / addStatsRecipient()
 * (backend zwraca snake_case). `created_at` to gotowy tekst `YYYY-MM-DD HH:MM` sformatowany
 * w bazie (StatsRecipients::CREATED_AT_FORMAT), a nie znacznik ISO - panel go tylko wyświetla.
 */
export const RecipientSchema = z.object({
    id: z.number(),
    email: z.string(),
    created_at: z.string(),
});
export type RecipientType = z.infer<typeof RecipientSchema>;

export const RecipientListSchema = z.array(RecipientSchema);

/**
 * Żądanie dodania odbiorcy. Format adresu sprawdza GUI przed wysłaniem oraz backend
 * (marker email_invalid), więc schemat pilnuje tylko niepustej wartości - wzorem
 * CreateUserRequestSchema z UserPermissionsApi.ts.
 */
export const AddRecipientRequestSchema = z.object({
    email: z.string().min(1),
});
export type AddRecipientRequestType = z.infer<typeof AddRecipientRequestSchema>;

export const RemoveRecipientRequestSchema = z.object({
    id: z.number(),
});
export type RemoveRecipientRequestType = z.infer<typeof RemoveRecipientRequestSchema>;

/** Odpowiedź Admin::removeStatsRecipient() - samo potwierdzenie usuniętego identyfikatora. */
export const RemovedRecipientSchema = z.object({
    id: z.number(),
});
export type RemovedRecipientType = z.infer<typeof RemovedRecipientSchema>;

/**
 * Markery odmów z backendu i odpowiadające im klucze i18n.
 *
 * `not_admin` pochodzi z PermissionGuard::assertIsAdmin(), `email_invalid` z
 * WebuserAccount::assertEmailAccepted(), `recipient_already_added` z
 * StatsRecipients::MARKER_RECIPIENT_EXISTS.
 */
const recipientErrorMessages: Record<string, string> = {
    email_invalid: 'Enter a valid e-mail address',
    recipient_already_added: 'This address is already on the list',
    not_admin: 'You are not allowed to manage stats recipients',
};

/**
 * Klucz i18n komunikatu dla odrzuconego żądania.
 *
 * Markera szukamy w TEKŚCIE odpowiedzi, nie w sparsowanym obiekcie - mechanika przepisana
 * z permissionErrorKey() (UserPermissions/UserPermissionsApi.ts). Backend oddaje `translationId`,
 * a vendor szuka `translation_id`, więc Api.getData() odrzuca pełnym błędem axiosa; przy odmowach
 * z metod statycznych gateway potrafi dodatkowo wyrenderować ciało błędu dwa razy (`{...}{...}`)
 * i wtedy odpowiedź nie jest poprawnym JSON-em, więc axios zostawia ją jako string.
 * Szukanie w tekście działa w obu wariantach.
 */
export function recipientErrorKey(error: unknown): string {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    const text = typeof data === 'string' ? data : JSON.stringify(data ?? '');
    const marker = Object.keys(recipientErrorMessages).find(key => text.includes(key));

    return marker ? recipientErrorMessages[marker] : 'Failed to update the recipient list';
}

export class StatsRecipientsApi {
    private static _endpoint: string = 'admin';

    /**
     * Puste ciało żądania ze schematem `z.object({})` to przyjęty w repo wzorzec dla operacji
     * bezparametrowych (UserPermissionsApi.getUsers).
     */
    public static async getRecipients(api: Api): Promise<RecipientType[]> {
        return api.getData<any, RecipientType[]>(
            'post',
            `/${this._endpoint}/get_stats_recipients`,
            {},
            z.object({}),
            RecipientListSchema
        );
    }

    /**
     * Odpowiedź to gotowy wiersz panelu - stan może go dołożyć u siebie bez ponownego pobrania
     * całości. Adres przycinamy z białych znaków (backend i tak normalizuje, ale dzięki temu
     * schemat `min(1)` odrzuca ciąg samych spacji jeszcze przed wysłaniem).
     */
    public static async addRecipient(api: Api, email: string): Promise<RecipientType> {
        const request: AddRecipientRequestType = { email: email.trim() };

        return api.getData<AddRecipientRequestType, RecipientType>(
            'post',
            `/${this._endpoint}/add_stats_recipient`,
            request,
            AddRecipientRequestSchema,
            RecipientSchema
        );
    }

    public static async removeRecipient(api: Api, id: number): Promise<RemovedRecipientType> {
        const request: RemoveRecipientRequestType = { id };

        return api.getData<RemoveRecipientRequestType, RemovedRecipientType>(
            'post',
            `/${this._endpoint}/remove_stats_recipient`,
            request,
            RemoveRecipientRequestSchema,
            RemovedRecipientSchema
        );
    }
}
